import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Grid,
  FormControlLabel,
  Radio,
  RadioGroup,
  IconButton,
  Select,
  MenuItem,
} from "@mui/material";
import Label from "../../../components/Label/Label";
import DatePicker from "react-datepicker";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import { useDispatch, useSelector } from "react-redux";
import {
  clearBatchListForReportState,
  fetchFilteredBatchListForReport,
} from "../../../redux/reports/actions";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Collapse from "@mui/material/Collapse";
import KeyboardArrowDownIcon from "@mui/icons-material/ArrowDropDown";
import KeyboardArrowUpIcon from "@mui/icons-material/ArrowDropUp";
import "./reports.scss";

const StepSelectBatch = ({ formData, onChange, setFormData, mode }) => {
  const dispatch = useDispatch();
  const lastPreviewPayload = useRef(null);

  const batchState = formData.batchState || {};
  const { data: batchData = [], loader: batchLoader } =
    useSelector((state) => state.reports.filteredBatchListForReport) || {};

  const [previewData, setPreviewData] = useState([]);
  const [fetchedOnce, setFetchedOnce] = useState(false);
  const [isPreviewCalledOnEditMode, setIsPreviewCalledOnEditMode] =
    useState(false);

  const updateState = (field, value) => {
    onChange({ [field]: value });
  };

  useEffect(() => {
    const batchDataFromDraftOrPreview =
      mode === "edit" && !isPreviewCalledOnEditMode
        ? formData.batchState?.draftBatchIdList
        : batchData;
    if (
      !batchLoader &&
      Array.isArray(batchDataFromDraftOrPreview) &&
      batchDataFromDraftOrPreview.length > 0
    ) {
      const previouslySelected = formData.batchState?.batchIdList || [];

      let allKeys = [];

      const initialized = batchDataFromDraftOrPreview.map((group) => {
        // updating batches based on previous selection
        const updatedBatches = group.data.map((batch) => {
          const isSelected =
            previouslySelected.length === 0
              ? true
              : mode === "edit" && !isPreviewCalledOnEditMode
                ? batch.isSelected
                : previouslySelected.includes(batch.batch_uop_key);

          if (isSelected) allKeys.push(batch.batch_uop_key);

          return { ...batch, isSelected };
        });
        // setting group selection based on batches - group is selected only if all batches are selected
        // setting data with updated batches
        return {
          ...group,
          isSelected: updatedBatches.every((b) => b.isSelected),
          isExpanded: false,
          data: updatedBatches,
        };
      });

      setPreviewData(initialized);

      if (previouslySelected.length === 0 && allKeys.length > 0) {
        updateState("batchIdList", allKeys);
      }
    }
  }, [batchLoader, batchData]);

  const handleRadioChange = (event, value) => {
    setFormData((prev) => ({
      ...prev,
      batchState: {
        ...prev.batchState,
        batchRangeOption: value,
        recentBatchCount: value === "by_latest_x" ? "20" : "",
        recentDayCount: "",
        startDate: null,
        endDate: null,
        batchIdList: [],
      },
    }));
    setPreviewData([]);
    dispatch(clearBatchListForReportState());
    setFetchedOnce(false);
  };

  const formatDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (isNaN(date)) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const onClickPreview = () => {
    const payload = {
      drug_product_id: formData?.product?.product_id,
      site_id: formData?.product?.site_id,
      batchs_selection: {
        batch_range_option: batchState?.batchRangeOption,
        recent_batch_count: batchState?.recentBatchCount,
        recent_day_count: batchState?.recentDayCount,
        start_date: formatDate(batchState?.startDate),
        end_date: formatDate(batchState?.endDate),
      },
    };

    setPreviewData([]);
    onChange({
      batchIdList: [],
      draftBatchIdList: [],
    });
    setFetchedOnce(true);
    dispatch(fetchFilteredBatchListForReport(payload));
    lastPreviewPayload.current = payload;
    if (mode === "edit") {
      setIsPreviewCalledOnEditMode(true);
    }
  };

  // ✅ UPDATE parent list
  const updateSelectedList = (updatedData) => {
    const selected = [];

    updatedData.forEach((g) =>
      g.data.forEach((b) => {
        if (b.isSelected) selected.push(b.batch_uop_key);
      }),
    );

    onChange({
      batchIdList: selected,
      draftBatchIdList: updatedData,
    });
  };

  const handleGroupChange = (gIndex, checked) => {
    const updated = [...previewData];
    // Group - setting group value based on user click true or false
    updated[gIndex].isSelected = checked;
    // Batches - setting all batches value based on group value - if group is true all batches should be true, if group is false all batches should be false
    updated[gIndex].data = updated[gIndex].data.map((b) => ({
      ...b,
      isSelected: checked,
    }));
    setPreviewData(updated);
    updateSelectedList(updated);
  };

  const handleBatchChange = (gIndex, bIndex) => {
    const updated = [...previewData];
    //Individual Batch -  setting batch value based on user click true or false
    updated[gIndex].data[bIndex].isSelected =
      !updated[gIndex].data[bIndex].isSelected;
    // Group -  group value should be true only if all batches are true, false otherwise
    updated[gIndex].isSelected = updated[gIndex].data.every(
      (b) => b.isSelected,
    );

    setPreviewData(updated);
    updateSelectedList(updated);
  };

  const toggleGroupExpand = (gIndex) => {
    const updated = [...previewData];
    updated[gIndex].isExpanded = !updated[gIndex].isExpanded;
    setPreviewData(updated);
  };

  const totalBatches = previewData.reduce(
    (acc, g) => acc + (g.data?.length || 0),
    0,
  );

  const totalSelectedBatches = previewData.reduce(
    (acc, g) => acc + g.data.filter((b) => b.isSelected).length,
    0,
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Card elevation={0} sx={{ flex: "0 0 auto" }}>
        <CardContent
          sx={{
            p: 0,
            "&:last-child": {
              pb: 0,
            },
          }}
        >
          <Grid item sx={{ mb: 1 }}>
            <Label text="Batch Range" fontWeight="700" fontSize="14px" />
          </Grid>

          <Grid
            container
            spacing={1}
            alignItems="center"
            sx={{ flexWrap: "nowrap" }}
          >
            <Grid item xs sx={{ minWidth: 0 }}>
              <RadioGroup
                row
                value={batchState.batchRangeOption}
                onChange={handleRadioChange}
                sx={{ flexWrap: "nowrap", overflowX: "auto" }}
              >
                <FormControlLabel
                  value="by_latest_x"
                  control={<Radio size="small" />}
                  label={
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Recent Batches"
                      value={batchState.recentBatchCount || ""}
                      onChange={(e) =>
                        updateState("recentBatchCount", e.target.value)
                      }
                      disabled={batchState.batchRangeOption !== "by_latest_x"}
                    />
                  }
                />

                <FormControlLabel
                  value="by_recent_x_days"
                  control={<Radio size="small" />}
                  label={
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Recent Days"
                      value={batchState.recentDayCount || ""}
                      onChange={(e) =>
                        updateState("recentDayCount", e.target.value)
                      }
                      disabled={
                        batchState.batchRangeOption !== "by_recent_x_days"
                      }
                    />
                  }
                />

                <FormControlLabel
                  value="by_date_x"
                  control={<Radio size="small" />}
                  className="batch-range-radio"
                  label={
                    <Box
                      sx={{ display: "flex", gap: 1 }}
                      className="date-range-picker-report"
                    >
                      {[
                        ["startDate", "From"],
                        ["endDate", "To"],
                      ].map(([key, placeholder]) => (
                        <DatePicker
                          key={key}
                          showYearDropdown
                          scrollableYearDropdown
                          yearDropdownItemNumber={50}
                          popperClassName="custom-datepicker-popper"
                          selected={
                            batchState[key] instanceof Date
                              ? batchState[key]
                              : null
                          }
                          onChange={(date) => updateState(key, date)}
                          selectsStart={key === "startDate"}
                          dateFormat="dd-MMM-yyyy"
                          selectsEnd={key === "endDate"}
                          startDate={
                            batchState.startDate instanceof Date
                              ? batchState.startDate
                              : null
                          }
                          endDate={
                            batchState.endDate instanceof Date
                              ? batchState.endDate
                              : null
                          }
                          minDate={
                            key === "endDate" &&
                            batchState.startDate instanceof Date
                              ? batchState.startDate
                              : undefined
                          }
                          maxDate={
                            key === "startDate"
                              ? batchState.endDate instanceof Date
                                ? batchState.endDate
                                : new Date()
                              : new Date()
                          }
                          placeholderText={placeholder}
                          disabled={batchState.batchRangeOption !== "by_date_x"}
                          fixedHeight
                          renderCustomHeader={({
                            date,
                            decreaseMonth,
                            increaseMonth,
                            changeYear,
                            prevMonthButtonDisabled,
                            nextMonthButtonDisabled,
                          }) => (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                px: 1.5,
                                py: 1,
                                bgcolor: "primary.main",
                                color: "common.white",
                                borderRadius: "8px 8px 0 0",
                              }}
                            >
                              <IconButton
                                onClick={decreaseMonth}
                                disabled={prevMonthButtonDisabled}
                                sx={{ color: "common.white", p: 0.5 }}
                                size="small"
                              >
                                <ArrowBackIosIcon fontSize="small" />
                              </IconButton>

                              <Box
                                sx={{
                                  mx: 1,
                                  fontWeight: 500,
                                  cursor: "default",
                                  width: 80,
                                  textAlign: "center",
                                }}
                              >
                                <Label
                                  text={date.toLocaleString("default", {
                                    month: "long",
                                  })}
                                  color="#ffff"
                                  fontSize={16}
                                  fontWeight={600}
                                />
                              </Box>

                              <Select
                                value={date.getFullYear()}
                                onChange={(e) =>
                                  changeYear(Number(e.target.value))
                                }
                                size="small"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                  fontSize: 13,
                                  color: "text.primary",
                                  bgcolor: "background.paper",
                                  borderRadius: 1,
                                  // "& .MuiSelect-select": { py: 0.25, px: 1 },
                                }}
                              >
                                {Array.from(
                                  { length: 50 },
                                  (_, i) => new Date().getFullYear() - i,
                                ).map((y) => (
                                  <MenuItem key={y} value={y}>
                                    {y}
                                  </MenuItem>
                                ))}
                              </Select>

                              <IconButton
                                onClick={increaseMonth}
                                disabled={nextMonthButtonDisabled}
                                sx={{ color: "common.white", p: 0.5 }}
                                size="small"
                              >
                                <ArrowForwardIosIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                          customInput={
                            <TextField
                              size="small"
                              fullWidth
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <CalendarTodayIcon fontSize="small" />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          }
                        />
                      ))}
                    </Box>
                  }
                />
              </RadioGroup>
            </Grid>

            {/* Preview Button */}
            <Grid item sx={{ flexShrink: 0 }}>
              <Button
                variant="outlined"
                startIcon={<PreviewOutlinedIcon />}
                onClick={onClickPreview}
              >
                <Label text="Preview" color="inherit" />
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Divider sx={{ my: 1 }} />

      <Box sx={{ flex: 1 }}>
        {batchLoader ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              minHeight: "200px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : previewData.length > 0 ? (
          <Box>
            <Box
              sx={{
                mb: 1,
              }}
            >
              <Label
                text={`Batch Preview (${totalSelectedBatches}/${totalBatches})`}
                fontWeight="500"
              />
            </Box>

            <TableContainer sx={{ maxHeight: "calc(100vh - 300px)" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#e0e0e0",
                      }}
                    >
                      <Label
                        text="Unit Operation / Batch"
                        fontSize="14px"
                        fontWeight="700"
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#e0e0e0",
                      }}
                    >
                      <Label
                        text="Batch Date"
                        fontSize="14px"
                        fontWeight="700"
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {previewData.map((group, gIndex) => (
                    <React.Fragment key={group["Unit Operation"]}>
                      {/* GROUP ROW */}
                      <TableRow
                        sx={{ background: "#f5f5f5", cursor: "pointer" }}
                        onClick={() => toggleGroupExpand(gIndex)}
                      >
                        <TableCell sx={{ padding: "4px 6px" }}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <IconButton size="small">
                              {group.isExpanded ? (
                                <KeyboardArrowUpIcon sx={{ fontSize: 20 }} />
                              ) : (
                                <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
                              )}
                            </IconButton>

                            <Checkbox
                              checked={group.isSelected}
                              indeterminate={
                                group.data.some((b) => b.isSelected) &&
                                !group.data.every((b) => b.isSelected)
                              }
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) =>
                                handleGroupChange(gIndex, e.target.checked)
                              }
                              sx={{ ml: 1, p: 0.5 }}
                            />

                            <Label
                              text={`${group["Unit Operation"]} (${
                                group.data.filter((b) => b.isSelected).length
                              } / ${group.data.length})`}
                              fontSize="13px"
                              fontWeight="600"
                            />
                          </Box>
                        </TableCell>

                        <TableCell />
                      </TableRow>

                      {/* BATCH ROWS (same table, no nested table) */}
                      {group.isExpanded &&
                        group.data.map((batch, bIndex) => (
                          <TableRow key={batch.batch_uop_key}>
                            <TableCell sx={{ padding: "4px 6px 4px 64px" }}>
                              <Checkbox
                                checked={batch.isSelected}
                                onChange={() =>
                                  handleBatchChange(gIndex, bIndex)
                                }
                                sx={{ mr: 1, p: 0.5 }}
                              />
                              {batch.batch_id_non_unique}
                            </TableCell>

                            <TableCell>{batch["Batch Date"]}</TableCell>
                          </TableRow>
                        ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : fetchedOnce ? (
          <Label textAlign="center" text="No batches available." />
        ) : (
          <Label textAlign="center" text="No batches fetched. Click Preview." />
        )}
      </Box>
    </Box>
  );
};

export default StepSelectBatch;
